import { useState } from "react";
import { Check, Copy, Link } from "lucide-react";
import { AccessManagement } from "./AccessManagement";
import { api, notify } from "./api";
import { Modal } from "./Modal";
import type { WorkspaceModel } from "./useWorkspace";

const roleDescription: Record<string, string> = {
  editor: "Can draw, edit project content and comment.",
  commenter: "Can join discussions and leave review marks.",
  viewer: "Can view project content only.",
};

export function ShareDialog({ model }: { model: WorkspaceModel }) {
  const { room, setModal, shareRole, setShareRole, shareUrl, setShareUrl } = model;
  const [expiresInDays, setExpiresInDays] = useState(7);
  const [expiresAt, setExpiresAt] = useState<number | null>(null);
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const copy = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      notify("Invitation link copied.");
    } catch {
      setCopied(false);
      notify("Copy unavailable. Select the link and copy it manually.");
    }
  };
  return (
    <Modal title="Share this project" close={() => setModal(null)}>
      <p>Invite people to collaborate on this project.</p>
      {room.role === "owner" ? (
        <>
          <div className="form-grid">
            <label>Invite as
              <select value={shareRole} onChange={(event) => {
                setShareRole(event.target.value);
                setShareUrl(""); setCopied(false);
              }}>
                <option value="editor">Editor</option>
                <option value="commenter">Commenter</option>
                <option value="viewer">Viewer</option>
              </select>
            </label>
            <label>Link expires after
              <select value={expiresInDays} onChange={(event) => {
                setExpiresInDays(Number(event.target.value));
                setShareUrl(""); setCopied(false);
              }}>
                <option value={1}>1 day</option>
                <option value={7}>7 days</option>
                <option value={30}>30 days</option>
              </select>
            </label>
          </div>
          <small>{roleDescription[shareRole]}</small>
          <button className="primary full" disabled={busy} onClick={async () => {
            setBusy(true);
            try {
              const result = await api<{ token: string; expiresAt: number }>(`/api/rooms/${room.id}/invites`, {
                role: shareRole, expiresInDays,
              });
              const url = `${location.origin}/?room=${room.id}${room.platform === "cloudflare" ? "#" : "&"}invite=${result.token}`;
              setShareUrl(url);
              setExpiresAt(result.expiresAt);
              setCopied(false);
              await copy(url);
            } catch (error) {
              notify((error as Error).message);
            } finally {
              setBusy(false);
            }
          }}><Link size={17} /> {busy ? "Creating link…" : "Create invitation link"}</button>
          {shareUrl && (
            <div className="share-result">
              <label>Invitation link
                <input aria-label="Invitation link" className="share-link" readOnly value={shareUrl} onFocus={(event) => event.target.select()} />
              </label>
              <button type="button" onClick={() => void copy(shareUrl)}>
                {copied ? <Check size={15} /> : <Copy size={15} />}{copied ? "Copied" : "Copy link"}
              </button>
              <small>{expiresAt ? `Expires ${new Date(expiresAt).toLocaleString()}.` : ""} Expiry only stops new joins; existing members keep their access.</small>
            </div>
          )}
          {room.platform === "cloudflare" && <AccessManagement roomId={room.id} />}
          {room.platform !== "cloudflare" && <small className="hint">This local preview link works on this computer. Remote access needs a hosted server.</small>}
        </>
      ) : <p>Ask the project owner to create an invitation.</p>}
    </Modal>
  );
}
